import {useEffect, useState, useContext} from "react";
import CountryContext from "../CountryContext";
import useCountryAxiosInstance from "./useCountryAxiosInstance";
import {NotificationsContext} from "../ui-components/Notifications";
import {useIntl} from "react-intl";
import {useServicesSurveys} from "./useServicesSurveys";
import AuthenticationService from "../services/AuthenticationService";
import apiRequest from "../services/apiRequest";
import {useWorksiteUsers} from "./useWorksiteUsers";
import {PAGINATION_TAKE} from "../pages/WorksitesTasks/constants";

export const useWorksiteData = (worksite) => {
	const intl = useIntl();
	const {country} = useContext(CountryContext);
	const countryApiRequest = useCountryAxiosInstance(country?.code);
	const {push} = useContext(NotificationsContext);
	const userData = AuthenticationService.getUserData();
	const userScopes = AuthenticationService.getUserScopes()

	const worksiteId = worksite.id
	const { servicesSurveys,  fetchServicesSurveys } = useServicesSurveys(worksiteId);
	const [sharing, setSharing] = useState(null);
	const [creators, setCreators] = useState(null);
	const [notesAttachments, setNotesAttachments] = useState(null);
	const [taskImages, setTaskImages] = useState(null);
	const [tasks, setTasks] = useState(null);
	const [products, setProducts] = useState(null);
	const [worksiteUsers] = useWorksiteUsers(worksite);
	const [userLoadedDocuments, setUserLoadedDocuments] = useState(null);
	const [quotationsAttachments, setQuotationAttachments] = useState()
	const moveUserDocument = async ({section, type, id, from, to}) => {

		const getTargetOrderValue = (list, index) => list[index]?.val_order;
		const reloadGroups = async () =>
			await countryApiRequest.get(`/worksite_documents/${worksiteId}`).then(setUserLoadedDocuments);

		let target, order
		switch (section) {
			case "users_documents":
				switch (type) {
					case "group":

						// Find the group to move and get the order value of the target index
						target = userLoadedDocuments.find(group => group.id === id);
						order = getTargetOrderValue(userLoadedDocuments, to.index);

						// Update the order of the group and reload all groups
						await countryApiRequest.put(`/worksite_documents/group/${id}`, {...target, val_order: order});
						await reloadGroups();

						break;
					case "document":

						// Find the document to move
						const sourceGroup = userLoadedDocuments.find(group => group.id === from.id);
						const destinationGroup = userLoadedDocuments.find(group => group.id === to.id);
						target = sourceGroup.documents.find(doc => doc.id === id);
						order = getTargetOrderValue(destinationGroup.documents, to.index) || 0;

						// Update the order of the document and reload all groups
						await countryApiRequest.put(
							`/worksite_documents/document/${id}`,
							{...target, val_order: order, id_group: to.id}
						);
						await reloadGroups();

						break;
					default:
						throw new Error(`Unhandled user document type ${type}`);
				}
				break;
			default:
				throw new Error(`Unhandled section ${section}`);
		}
	}

	useEffect(async () => {
		const [
			notesAttachmentsres,
			taskImagesres,
			tasksres,
			taskMap,
			productsres,
			userLoadedDocumentsres,
			quotationsAttachments
		] = await Promise.all([
			countryApiRequest.get(`/worksites_notes/all`, {params: {cod_worksite: worksiteId, flg_deleted: false}}),
			countryApiRequest.get(`/images/all?`, {params: {id_worksite: worksiteId}}),
			countryApiRequest.get("/worksites_tasks/all_paginated", {params: {id_worksite: worksiteId}}),
			countryApiRequest.get("/worksites_tasks_maps/all_counted", {
				params: {
					id_worksite: worksiteId,
					join: "image",
					desc: "created_at",
				}
			}),
			countryApiRequest.get(`/service_surveys_kpi/worksite_products/${worksiteId}`),
			countryApiRequest.get(`/worksite_documents/${worksiteId}`),
			userScopes?.includes('quotation:read')?countryApiRequest.get(`/quotations/attachments/${worksiteId}`): []
		]);
		setNotesAttachments(notesAttachmentsres.flatMap(note => note.attachments_urls?.data.map(na => ({
			...na,
			cod_author: note.cod_author
		})) ?? []));

		setTaskImages(taskImagesres);
		setTasks(tasksres.items.map(task => ({
			...task,
			task_map_image: taskMap.find(tm => tm.task_map.id === task.id_task_map)?.task_map?.image
		})));
		setProducts(productsres);
		setUserLoadedDocuments(userLoadedDocumentsres);
		setQuotationAttachments(quotationsAttachments);
	}, [worksite, countryApiRequest]);  // eslint-disable-line react-hooks/exhaustive-deps

	  const fetchSharing = () => {
    countryApiRequest
			.post("/share/from_worksites", {ids: [worksite["id"]]})
      .then((res) => {
        setSharing((res || []).length ? res[0]["sharing"] : []);
      })
      .catch((err) => {
        console.log("errore", err);
      });
  };
  useEffect(() => {
    if (worksite) {
      fetchSharing();
    }
    // eslint-disable-next-line
  }, [worksite]);

  useEffect(() => {
    if (worksite && sharing && servicesSurveys) {
      let ids_creators = [
        ...new Set([worksite["id_creator"], userData.id]),
      ];
      Object.values(sharing).forEach((j) => {
        if (!ids_creators.includes(j["to_user_id"])) {
          ids_creators.push(j["to_user_id"]);
        }
      });
      Object.values(servicesSurveys).forEach((j) => {
        ["id_creator", "id_modifier"].forEach((k) => {
          if (j[k] && !ids_creators.includes(j[k])) {
            ids_creators.push(j[k]);
          }
        });
      });

      const fetchCreators = () => {
        apiRequest.post("/creators", { ids: ids_creators }).then((res) => {
					setCreators({
						...creators,
						...(res || []).reduce((obj, user) => _.set(obj, [user.id], user), {})
					});
        });
      };

      fetchCreators();
    }
    // eslint-disable-next-line
  }, [sharing, worksite, servicesSurveys]);

	return {
		worksite,
		notesAttachments,
		servicesSurveys,
		taskImages,
		tasks,
		products,
		userLoadedDocuments,
		setUserLoadedDocuments,
		moveUserDocument,
		sharing,
		creators,
		worksiteUsers,
		quotationsAttachments,
	};
}